import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
} from "@mui/material";
import AnimatedText from "./AnimatedText";
import { usePublishFolder } from "../hooks/usePublishFolder";
import AdminReviewDialog from "./AdminReviewDialog";
/**
 * RootFolder component displays the subfolders of a selected folder.
 * Props:
 * - folder: the selected folder object containing subfolders
 * - onSubfolderSelect: callback when a subfolder is selected
 * - selectedSubfolderId: id of the currently selected subfolder
 */
export default function RootFolder({
  folder,
  subfolders = [],
  onSubfolderSelect,
  selectedSubfolderId,
}) {
  const {
    publishFolder,
    isPublishing,
    publishSuccess,
    resetPublishSuccess,
    error,
  } = usePublishFolder();

  const {
    status,
    adminNotes,
    approvedBy,
    approvedAt,
    rejectedBy,
    rejectedAt,
    adminAvatarPath,
  } = folder || {};

  console.log("[DEBUG]: Folder status: ", status);
  useEffect(() => {
    if (publishSuccess) {
      const timer = setTimeout(() => resetPublishSuccess(), 4000);
      return () => clearTimeout(timer);
    }
  }, [publishSuccess, resetPublishSuccess]);

  if (!folder) {
    return <Typography>Select a folder to view its subfolders.</Typography>;
  }

  if (subfolders.length === 0) {
    return <Typography>No subfolders found in this folder.</Typography>;
  }

  // Split folders: 4 on top, 3 on bottom
  const topRow = subfolders.slice(0, 4);
  const bottomRow = subfolders.slice(4, 8);

  const handlePublish = async () => {
    await publishFolder(folder.folderId);
  };

  const handleCloseSnackbar = () => {
    resetPublishSuccess();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 5,
        width: "100%",
        pb: 10,
      }}
    >
      <AnimatedText text="Accreditation Areas" />
      {/* Top row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 3,
          mb: 3,
        }}
      >
        {topRow.map((subfolder) => (
          <FolderCard
            key={subfolder.id}
            folder={subfolder}
            subfolderName={subfolder.subfolderName}
            onClick={() => onSubfolderSelect(subfolder)}
          />
        ))}
      </Box>
      {/* Bottom row, centered under top row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 3,
        }}
      >
        {bottomRow.map((subfolder) => (
          <FolderCard
            key={subfolder.id}
            folder={subfolder}
            subfolderName={subfolder.subfolderName}
            onClick={() => onSubfolderSelect(subfolder)}
          />
        ))}
      </Box>
      {/* Footer with Publish Folder button */}
      {/* Admin Review Dialog */}
      {status !== "pending" && status !== "draft" && (
        <AdminReviewDialog
          adminNotes={adminNotes}
          adminName={approvedBy || rejectedBy || "Admin"}
          adminAvatarPath={adminAvatarPath}
          timestamp={approvedAt || rejectedAt}
          sentiment={
            status === "approved"
              ? "approved"
              : status === "rejected"
              ? "rejected"
              : "pending"
          }
        />
      )}

      <Box
        component="footer"
        sx={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100%",
          zIndex: (theme) => theme.zIndex.appBar,
          py: 2,
          backgroundColor: "rgba(9, 13, 15, 0.95)",
          display: "flex",
          justifyContent: "center",
          boxShadow: "0 -2px 12px 0 rgba(0,0,0,0.18)",
        }}
      >
        {/* Custom alert above the button */}
        {publishSuccess && (
          <Box
            sx={{
              position: "absolute",
              bottom: "80px", // adjust as needed, e.g. height of button + gap
              left: "55.5%",
              transform: "translateX(-50%)",
              width: "fit-content",
              minWidth: 300,
              zIndex: 1,
            }}
          >
            <Alert
              severity="success"
              onClose={handleCloseSnackbar}
              sx={{
                width: "100%",
                boxShadow: 2,
              }}
            >
              Folder published successfully!
            </Alert>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={handlePublish}
          sx={{
            marginLeft: 30,
            opacity:
              folder.status === "pending" || folder.status === "approved"
                ? 1 // keep fully visible, or set to 0.8 if you want a little fade
                : 1,
            pointerEvents:
              folder.status === "pending" || folder.status === "approved"
                ? "none"
                : "auto",
            backgroundColor:
              folder.status === "pending" || folder.status === "approved"
                ? "#f5f5f5" // Inactive: offwhite
                : "#1DB954",
            color:
              folder.status === "pending" || folder.status === "approved"
                ? "#aaa" // or a muted/gray color to show it's inactive
                : "#fff",
            boxShadow: "none", // optional: makes it flatter when inactive
            border:
              folder.status === "pending" || folder.status === "approved"
                ? "1px solid #ddd"
                : "1px solid #c5e1a5",
            transition: "background 0.2s",
            "&:hover": {
              color: " 	#1B1212",
              backgroundColor:
                folder.status === "pending" || folder.status === "approved"
                  ? "#f5f5f5"
                  : "#D2F8D2", // Keeps the same color when hovered!
            },
          }}
        >
          {isPublishing
            ? "Publishing..."
            : folder.status === "rejected"
            ? "Re-Publish"
            : "Publish Folder"}
        </Button>
      </Box>
    </Box>
  );
}
// FolderCard component for reusability and clarity
function FolderCard({ folder, subfolderName, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: 3,
        borderRadius: 3.5,
        backgroundColor: "#E6E6E6",
        border: "1.5px solid #e3e7ed",
        boxShadow: "0 2px 8px 0 rgba(60, 60, 60, 0.66)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 210,
        minHeight: 132,
        transition: "background 0.2s, box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          backgroundColor: "#f5f9ff",
          borderColor: "#a2c9fa",
          boxShadow: "0 6px 24px 0 rgba(60,120,240,0.11)",
        },
      }}
    >
      {/* Slightly bigger, blue-ish folder icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="52"
        height="52"
        fill="none"
        viewBox="0 0 24 24"
        stroke="#4294ff"
        strokeWidth="1.4"
        style={{ marginBottom: 14 }}
      >
        <path
          d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z"
          fill="#e7f1ff"
        />
        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
      </svg>
      <Typography
        variant="body1"
        sx={{
          color: "#222b45",
          fontWeight: 600,
          textAlign: "center",
          wordBreak: "break-word",
          fontSize: 17.5,
          letterSpacing: "0.01em",
        }}
      >
        {subfolderName}
      </Typography>
    </Box>
  );
}
